import {
  useState,
  useEffect,
  useRef,
  useMemo,
  type ChangeEvent,
  type KeyboardEvent,
} from "react"
import {
  Plus,
  ArrowUp,
  X,
  FileText,
  ChevronDown,
  Maximize2,
} from "lucide-react"
import { VoxnelaMark } from "../../components/ui/VoxnelaMark"
import type {
  AspectRatio,
  StudioAsset,
  StudioMode,
} from "../domain/studioTypes"
import { useStudioWorkspace } from "../contexts/StudioWorkspaceContext"
import { ALL_APPLICATION_STYLES } from "../../data/applicationStyleCatalog"
import type { VoxnelaTaskItem } from "../../data/voxnela56Tasks"
import { uploadStudioAsset } from "../lib/studioApi"
import { isHeicFile } from "../lib/imageCompressor"
import { ASPECT_RATIO_OPTIONS } from "../data/studioCatalog"
import {
  StudioImageDetailSheet,
  type StudioImageDetailItem,
} from "./StudioImageDetailSheet"
import { getProjectStyleTitle } from "../lib/studioImageDetails"
import { haptic } from "../../lib/native"
import { useVoxnela } from "../../contexts/VoxnelaContext"
import { paywallMessages } from '../../i18n/paywall'
import { useAuth } from "../../contexts/AuthContext"
import { requestGenerationSignIn } from "../../lib/authRequired"
import { QuotaExhaustedModal } from "../../components/membership/QuotaExhaustedModal"
import { PaywallSheet } from "../../components/membership/PaywallSheet"
import type { PaidMembershipPlan } from "../../lib/billingCatalog"
import { TaskShowcaseModal } from "../../components/tasks/TaskShowcaseModal"
import { VoxnelaAlertModal } from "../../components/ui/VoxnelaAlertModal"
import { useUiText } from "../../i18n/ui"
import { formatStudioMessage, studioMessages } from "../../i18n/studio"
import { pickStudioQuickStartIdeas, studioQuickStartIdeas, studioQuickStartIdeasLabel, studioPremiumPlaceholder } from "../../i18n/studioQuickStartIdeas"
import { localizeTasks } from "../../i18n/catalog"
import { NEXT_YEARS_STYLE_ID, nextYearsImagePrompt, parseCurrentAge } from "../lib/nextYears"
import { generationConfirmationCopy } from "../lib/generationConfirmationCopy"

interface ChatMessage {
  id: string
  sender: "user" | "ai"
  text?: string
  styleTitle?: string
  aspectRatio?: AspectRatio
  assets?: StudioAsset[]
  imageUrl?: string
  version?: number
  isGenerating?: boolean
  timestamp: number
  projectId?: string
}

interface ActiveReference {
  projectId?: string
  imageUrl: string
  variation?: boolean
  aspectRatio?: AspectRatio
  entryOrigin?: 'gallery'
  displayTitle?: string
  description?: string
  version?: number
  label?: string
}

interface PendingUpload {
  id: string
  name: string
  isImage: boolean
}

interface StudioNotice {
  title: string
  message: string
}

interface GenerationConfirmation {
  styleTitle: string | null
  instructions: string
}

const ALL_ASSET_TYPES = "image/*,.heic,.heif,.pdf,.docx,.pptx,.txt"
const COLLAGE_STYLE_IDS = new Set(['creative_photo_collage', 'creative_memory_collage'])
const CLOTHING_TRYON_STYLE_ID = 'voxnela_clothing_tryon'
const TIMELINE_STYLE_ID = 'editorial_visual_timeline'

export function StudioSheetContent() {
  const { locale } = useUiText()
  const copy = studioMessages[locale]
  const applicationStyles = useMemo(() => localizeTasks(ALL_APPLICATION_STYLES, locale), [locale])
  const {
    projects = [],
    sheetMode,
    setSheetMode,
    selectedSubcategory,
    setSelectedSubcategory,
    activeStylePreset,
    styleEntryOrigin,
    setActiveStylePreset,
    activeReferencePreset,
    setActiveReferencePreset,
    activeProject,
    activeProjectId,
    clearActiveProject,
    createProject,
    reviseProject,
    isGenerating,
    isRevising,
  } = useStudioWorkspace()

  const [prompt, setPrompt] = useState("")
  const [quickStartIdeaIndexes] = useState(pickStudioQuickStartIdeas)
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<AspectRatio>("9:16")
  const [aspectMenuOpen, setAspectMenuOpen] = useState(false)
  const [assets, setAssets] = useState<StudioAsset[]>([])
  const [uploading, setUploading] = useState(false)
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([])
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [activeReference, setActiveReference] = useState<ActiveReference | null>(null)
  const [activeDetailIndex, setActiveDetailIndex] = useState<number | null>(null)
  const [showStyleDetails, setShowStyleDetails] = useState(Boolean(activeStylePreset))
  const [inputFocused, setInputFocused] = useState(false)
  const [inspectStyleTask, setInspectStyleTask] = useState<VoxnelaTaskItem | null>(null)
  const [nextYearsAge, setNextYearsAge] = useState<number | null>(null)
  const [isNextYearsGenerating, setIsNextYearsGenerating] = useState(false)
  const nextYearsRequestInFlightRef = useRef(false)
  const lastSelectedStyleIdRef = useRef<string | null>(null)

  const chatImageItems = useMemo<StudioImageDetailItem[]>(() => {
    return messages
      .filter((m) => m.sender === "ai" && Boolean(m.imageUrl) && !m.isGenerating)
      .map((m) => {
        const proj = m.projectId
          ? projects.find((p) => p.id === m.projectId)
          : activeProjectId
            ? projects.find((p) => p.id === activeProjectId)
            : undefined

        return {
          id: m.id,
          imageUrl: proj?.versions.find((v) => v.version === m.version)?.imageUrl || m.imageUrl!,
          title: m.styleTitle || (proj ? getProjectStyleTitle(proj, copy.defaultStyle, locale) : undefined),
          subtitle: m.version ? `v${m.version}` : undefined,
          version: m.version,
          projectId: m.projectId || proj?.id || activeProjectId || undefined,
          project: proj,
          aspectRatio: m.aspectRatio,
          createdAtMs: m.timestamp,
        }
      })
  }, [messages, projects, activeProjectId, copy.defaultStyle, locale])

  const imageUrlForMessage = (message: { imageUrl?: string; projectId?: string; version?: number }) =>
    projects.find((project) => project.id === (message.projectId || activeProjectId))
      ?.versions.find((version) => version.version === message.version)?.imageUrl || message.imageUrl || ''

  const { credits, membership, membershipSyncPending, membershipLoading, workspaceError, useCredit: consumeCredit, settleCredit, refundCredit } = useVoxnela()
  const { user } = useAuth()
  const [quotaModalOpen, setQuotaModalOpen] = useState(false)
  const [paywallOpen, setPaywallOpen] = useState(false)
  const [paywallInitialPlan, setPaywallInitialPlan] = useState<PaidMembershipPlan | undefined>()
  const [notice, setNotice] = useState<StudioNotice | null>(null)
  const [generationConfirmation, setGenerationConfirmation] = useState<GenerationConfirmation | null>(null)
  const confirmationCopy = generationConfirmationCopy[locale]
  const confirmationApprovedRef = useRef(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const chatStreamRef = useRef<HTMLDivElement>(null)
  const aspectMenuRef = useRef<HTMLDivElement>(null)

  // Sync with active reference preset (when an image was clicked in Gallery)
  useEffect(() => {
    if (activeReferencePreset) {
      if (activeReferencePreset.aspectRatio) setSelectedAspectRatio(activeReferencePreset.aspectRatio)
      if (activeReferencePreset.entryOrigin === 'gallery') {
        setMessages([])
        setAssets([])
        setPrompt('')
        setInputFocused(false)
      }
      // The gallery can replace the active reference while this sheet stays mounted.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActiveReference(activeReferencePreset)
      setActiveReferencePreset(null)
    }
  }, [activeReferencePreset, setActiveReferencePreset])

  // Sync aspect ratio and subcategory from active project
  useEffect(() => {
    if (activeProject) {
      if (activeProject.aspectRatio) {
        // A reopened project owns the composer ratio.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedAspectRatio(activeProject.aspectRatio)
      }
    }
  }, [activeProject])

  // A style opened from Home/Explore should reveal its own description.
  useEffect(() => {
    if (activeStylePreset) {
      // An external style selection can arrive while the shared sheet stays mounted.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowStyleDetails(true)
    }
  }, [activeStylePreset])

  useEffect(() => {
    const selectedStyleId = activeStylePreset?.id ?? null
    if (lastSelectedStyleIdRef.current === selectedStyleId) return
    lastSelectedStyleIdRef.current = selectedStyleId
    setNextYearsAge(null)
  }, [activeStylePreset])

  // Close the dropdown for every mouse, pen and touch interaction outside it.
  useEffect(() => {
    if (!aspectMenuOpen) return
    const handlePointerOutside = (event: PointerEvent) => {
      const target = event.target
      if (target instanceof Node && aspectMenuRef.current && !aspectMenuRef.current.contains(target)) {
        setAspectMenuOpen(false)
      }
    }
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setAspectMenuOpen(false)
    }
    const sheetScroller = aspectMenuRef.current?.closest<HTMLElement>(".float-island-sheet-body")
    const handleScroll = () => setAspectMenuOpen(false)

    document.addEventListener("pointerdown", handlePointerOutside, true)
    document.addEventListener("keydown", handleEscape)
    sheetScroller?.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      document.removeEventListener("pointerdown", handlePointerOutside, true)
      document.removeEventListener("keydown", handleEscape)
      sheetScroller?.removeEventListener("scroll", handleScroll)
    }
  }, [aspectMenuOpen])

  // Scroll to bottom
  useEffect(() => {
    if (chatStreamRef.current) {
      chatStreamRef.current.scrollTop = chatStreamRef.current.scrollHeight
    }
  }, [messages, isGenerating])

  // The selected style stays separate from the user's own text.
  const activeStyle = useMemo(() => {
    if (activeStylePreset) {
      return {
        id: activeStylePreset.id,
        title: activeStylePreset.title,
        imageUrl: activeStylePreset.imageUrl,
        category: activeStylePreset.category,
        description: activeStylePreset.description || "",
        suggestions: activeStylePreset.suggestions || [],
        realPrompt: activeStylePreset.realPrompt || "",
        mode: activeStylePreset.mode || sheetMode || "style_transfer",
        subcategoryId: activeStylePreset.subcategoryId || activeStylePreset.id,
      }
    }
    return null
  }, [activeStylePreset, sheetMode])

  const handleClearActiveStyle = () => {
    void haptic("selection")
    setActiveStylePreset(null)
    setSelectedSubcategory(null)
    setShowStyleDetails(false)
    setNextYearsAge(null)
  }

  const handleSelectCatalogStyle = (style: VoxnelaTaskItem) => {
    void haptic("selection")
    setSheetMode(style.studioMode)
    setSelectedSubcategory(style.recommendedStyleId || style.id)
    setSelectedAspectRatio(style.aspectRatio)
    setActiveStylePreset({
      id: style.id,
      title: style.title,
      imageUrl: style.sampleImageUrl,
      category: style.category,
      description: style.description,
      realPrompt: style.promptTemplate,
      suggestions: style.suggestions,
      mode: style.studioMode,
      subcategoryId: style.recommendedStyleId || style.id,
      aspectRatio: style.aspectRatio,
    })
    setShowStyleDetails(true)
    setNextYearsAge(null)
  }

  const openAssetPicker = () => {
    const input = fileInputRef.current
    if (!input) return
    input.accept = (COLLAGE_STYLE_IDS.has(activeStyle?.id ?? '') || activeStyle?.id === CLOTHING_TRYON_STYLE_ID) ? 'image/*,.heic,.heif' : ALL_ASSET_TYPES
    input.multiple = true
    input.click()
  }

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const existingImageCount = assets.filter((a) => a.kind === "image").length
      + ((COLLAGE_STYLE_IDS.has(activeStyle?.id ?? '') || activeStyle?.id === CLOTHING_TRYON_STYLE_ID) && activeReference?.imageUrl ? 1 : 0)
    const newFiles = Array.from(files)
    const isClothingTryOnStyle = activeStyle?.id === CLOTHING_TRYON_STYLE_ID
    if ((COLLAGE_STYLE_IDS.has(activeStyle?.id ?? '') || isClothingTryOnStyle) && newFiles.some((file) => !file.type.startsWith('image/') && !isHeicFile(file))) {
      setNotice({ title: isClothingTryOnStyle ? activeStyle?.title ?? 'Kıyafet Dene' : 'Kolaj', message: locale === 'tr' ? 'Lütfen yalnızca görsel ekleyin.' : 'Please add images only.' })
      if (fileInputRef.current) fileInputRef.current.value = ""
      return
    }

    // Check 12 MB limit
    const overSized = newFiles.find((f) => f.size > 12 * 1024 * 1024)
    if (overSized) {
      setNotice({
        title: copy.fileLimitTitle,
        message: formatStudioMessage(copy.fileLimitMessage, { name: overSized.name, size: (overSized.size / (1024 * 1024)).toFixed(1) }),
      })
      if (fileInputRef.current) fileInputRef.current.value = ""
      return
    }

    // Try-on takes one portrait plus up to four garment photos; collage remains capped at four.
    const incomingImages = newFiles.filter((f) => f.type.startsWith("image/") || isHeicFile(f))
    const imageLimit = isClothingTryOnStyle ? 5 : 4
    if (existingImageCount + incomingImages.length > imageLimit) {
      setNotice({
        title: copy.imageLimitTitle,
        message: isClothingTryOnStyle
          ? (locale === 'tr' ? 'Bir tam boy fotoğraf ve en fazla 4 kıyafet görseli ekleyebilirsin.' : 'Add one full-length portrait and up to 4 clothing images.')
          : formatStudioMessage(copy.imageLimitMessage, { count: existingImageCount }),
      })
      if (fileInputRef.current) fileInputRef.current.value = ""
      return
    }

    const pendingItems: PendingUpload[] = newFiles.map((file, idx) => ({
      id: `pending_${Date.now()}_${idx}_${file.name}`,
      name: file.name,
      isImage: file.type.startsWith("image/") || isHeicFile(file),
    }))
    setPendingUploads(pendingItems)
    setUploading(true)

    const tempProjectId = `temp_${Date.now()}`
    try {
      for (let i = 0; i < newFiles.length; i++) {
        const file = newFiles[i]
        const pendingItem = pendingItems[i]
        if (!file || !pendingItem) continue
        const asset = await uploadStudioAsset(file, tempProjectId)
        setAssets((prev) => [...prev, asset])
        setPendingUploads((prev) => prev.filter((p) => p.id !== pendingItem.id))
      }
      void haptic("success")
    } catch (err: unknown) {
      console.warn("Dosya yükleme hatası:", err)
      setNotice({
        title: copy.uploadFailedTitle,
        message: err instanceof Error ? err.message : copy.uploadFailedMessage,
      })
    } finally {
      setPendingUploads([])
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }

  const removeAsset = (assetId: string) => {
    void haptic("selection")
    setAssets((prev) => prev.filter((a) => a.id !== assetId))
  }

  const handleClearReference = () => {
    void haptic("selection")
    setActiveReference(null)
    clearActiveProject()
  }

  const handleSend = async (confirmed = false) => {
    if (generationConfirmation && !confirmed) return
    const textToSend = prompt.trim()
    const isAnalysisStyle = ['voxnela_face_style_analysis', 'voxnela_outfit_analysis', 'voxnela_combin_analysis', CLOTHING_TRYON_STYLE_ID].includes(activeStyle?.id ?? '')
    const isClothingTryOnStyle = activeStyle?.id === CLOTHING_TRYON_STYLE_ID
    const isCollageStyle = COLLAGE_STYLE_IDS.has(activeStyle?.id ?? '')
    const isTimelineStyle = activeStyle?.id === TIMELINE_STYLE_ID
    const isRevisingTarget = Boolean(activeReference) && !isAnalysisStyle && !isCollageStyle && !isTimelineStyle
    const currentProjectId = activeReference?.projectId || activeProjectId
    const referenceImageToSend = activeReference?.imageUrl
    const hasDistinctReference = Boolean(referenceImageToSend && !assets.some((asset) => asset.kind === 'image' && asset.url === referenceImageToSend))

    if (activeStyle?.id === NEXT_YEARS_STYLE_ID) {
      if (nextYearsRequestInFlightRef.current || isNextYearsGenerating || uploading || isGenerating) return
      const suppliedAge = parseCurrentAge(textToSend)
      const currentAge = suppliedAge ?? nextYearsAge
      if (!currentAge) {
        setNotice({ title: activeStyle.title, message: locale === 'tr' ? 'Next Years için yaşınızı yazın.' : 'Enter your age for Next Years.' })
        return
      }
      const sourceImages = assets.filter((asset) => asset.kind === 'image').slice(0, 1)
      if (sourceImages.length === 0 && referenceImageToSend) {
        sourceImages.push({ id: `next_years_reference_${Date.now()}`, name: 'portrait.png', url: referenceImageToSend, mimeType: 'image/png', sizeBytes: 0, kind: 'image' })
      }
      if (sourceImages.length === 0) {
        setNotice({ title: activeStyle.title, message: locale === 'tr' ? 'Yüzünüzün net göründüğü güncel bir fotoğraf ekleyin.' : 'Add a recent photo with your face clearly visible.' })
        setPrompt('')
        return
      }
      if (!confirmed) {
        confirmationApprovedRef.current = false
        setGenerationConfirmation({ styleTitle: activeStyle.title, instructions: textToSend })
        return
      }
      if (!user) {
        requestGenerationSignIn()
        return
      }
      if (membershipSyncPending || membershipLoading || workspaceError) {
        setNotice({ title: paywallMessages[locale].membership, message: (workspaceError || membershipSyncPending ? paywallMessages[locale].membershipSyncFailed : paywallMessages[locale].membershipSyncPending) })
        return
      }
      if (credits < 1) {
        setQuotaModalOpen(true)
        return
      }
      nextYearsRequestInFlightRef.current = true
      setNextYearsAge(currentAge)
      setShowStyleDetails(false)
      const reservationKey = `next_years_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
      if (!consumeCredit(reservationKey)) {
        nextYearsRequestInFlightRef.current = false
        setQuotaModalOpen(true)
        return
      }
      const runId = Date.now()
      const loadingMessageId = `next_years_${runId}`
      setMessages((current) => [
        ...current,
        { id: `user_${runId}`, sender: 'user', text: `${currentAge} yaşındayım`, assets: sourceImages, styleTitle: activeStyle.title, timestamp: runId },
        { id: loadingMessageId, sender: 'ai', isGenerating: true, text: '', timestamp: runId + 1 },
      ])
      setPrompt('')
      setIsNextYearsGenerating(true)
      try {
        const project = await createProject({
          title: activeStyle.title,
          mode: 'style_transfer',
          subcategory: NEXT_YEARS_STYLE_ID,
          styleCategory: activeStyle.category,
          prompt: nextYearsImagePrompt(currentAge, suppliedAge ? '' : textToSend),
          stylePrompt: activeStyle.realPrompt,
          currentAge,
          aspectRatio: '9:16',
          inputAssets: sourceImages,
        })
        settleCredit(reservationKey)
        setMessages((current) => current.map((message) => message.id === loadingMessageId
          ? { ...message, isGenerating: false, imageUrl: project.versions[0]?.imageUrl, projectId: project.id, version: 1, aspectRatio: '9:16', styleTitle: activeStyle.title, text: undefined }
          : message))
        setAssets([])
        setActiveReference(null)
        void haptic('success')
      } catch (error) {
        refundCredit(reservationKey)
        setMessages((current) => current.filter((message) => message.id !== loadingMessageId))
        setNotice({ title: activeStyle.title, message: error instanceof Error ? error.message : copy.generationFailed })
        console.warn('[Voxnela Studio] Next Years görseli üretilemedi.', error)
      } finally {
        nextYearsRequestInFlightRef.current = false
        setIsNextYearsGenerating(false)
      }
      return
    }

    if (isAnalysisStyle && !assets.some((asset) => asset.kind === 'image') && !referenceImageToSend) {
      setNotice({ title: activeStyle?.title ?? copy.imageCreation, message: locale === 'tr' ? 'Analiz için net bir fotoğraf ekleyin. Kombin için tam boy; yüz analizi için yüzünüzün net göründüğü bir fotoğraf seçin.' : 'Add a clear photo. Use a full-length photo for outfits or a clear face portrait for face analysis.' })
      return
    }

    if (isClothingTryOnStyle) {
      const clothingTryOnImageCount = assets.filter((asset) => asset.kind === 'image').length + (hasDistinctReference ? 1 : 0)
      if (clothingTryOnImageCount < 2 || clothingTryOnImageCount > 5) {
        setNotice({ title: activeStyle?.title ?? 'Kıyafet Dene', message: locale === 'tr' ? 'Önce kendine ait tam boy fotoğrafını, ardından 1–4 kıyafet görselini ekle.' : 'Add your full-length portrait first, followed by 1–4 clothing images.' })
        return
      }
    }

    if (isCollageStyle) {
      const collageImageCount = assets.filter((asset) => asset.kind === 'image').length + (hasDistinctReference ? 1 : 0)
      if (collageImageCount < 2 || collageImageCount > 4) {
        setNotice({ title: 'Kolaj', message: locale === 'tr' ? 'Lütfen en az 2, en fazla 4 görsel ekleyin.' : 'Please add at least 2 and no more than 4 images.' })
        return
      }
    }

    if (isTimelineStyle && !textToSend && assets.length === 0 && !referenceImageToSend) {
      setNotice({ title: locale === 'tr' ? 'Zaman Çizelgesi' : 'Timeline', message: locale === 'tr' ? 'Bir metin yazın veya belge ya da görsel ekleyin.' : 'Enter text or add a document or image.' })
      return
    }

    let instructionToSend = textToSend
    if (!textToSend && !activeStyle && (assets.length > 0 || referenceImageToSend)) {
      instructionToSend = "Use the uploaded image as the primary visual reference and create a polished professional result."
    }
    if (activeReference?.variation) {
      instructionToSend = `Create a distinct new variation of the supplied reference image. Preserve its core subject and recognizable identity while changing the composition, lighting, styling, and supporting details. Do not simply duplicate the source.\n\n${instructionToSend}`
    }
    if (isTimelineStyle && textToSend) {
      instructionToSend = `TIMELINE_SOURCE_TEXT_BEGIN\n${textToSend}\nTIMELINE_SOURCE_TEXT_END`
    }
    if (!instructionToSend && activeStyle) {
      instructionToSend = 'Create this style.'
    }

    if (!instructionToSend && assets.length === 0 && !referenceImageToSend && !activeStyle) return
    if (isGenerating || isRevising || uploading) return

    if (!user) {
      void haptic("medium")
      requestGenerationSignIn()
      return
    }
    if (!isRevisingTarget && !confirmed) {
      confirmationApprovedRef.current = false
      setGenerationConfirmation({ styleTitle: activeStyle?.title ?? null, instructions: textToSend })
      return
    }
    if (membershipSyncPending || membershipLoading || workspaceError) {
      setNotice({ title: paywallMessages[locale].membership, message: (workspaceError || membershipSyncPending ? paywallMessages[locale].membershipSyncFailed : paywallMessages[locale].membershipSyncPending) })
      return
    }
    if (credits <= 0) {
      void haptic("medium")
      setQuotaModalOpen(true)
      return
    }

    const idempotencyKey = `gen_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
    const creditGranted = consumeCredit(idempotencyKey)
    if (!creditGranted) {
      void haptic("medium")
      setQuotaModalOpen(true)
      return
    }

    void haptic("selection")
    setAspectMenuOpen(false)

    const targetMode: StudioMode = activeStyle?.mode || sheetMode || "style_transfer"
    const targetSubcat = activeStyle?.subcategoryId || selectedSubcategory || "default"
    const aspectRatio: AspectRatio = selectedAspectRatio

    const userDisplayText = textToSend
      ? textToSend
      : activeStyle
        ? formatStudioMessage(copy.styleApplied, { style: activeStyle.title })
        : copy.imageCreation

    const userMessage: ChatMessage = {
      id: `user_${Date.now()}`,
      sender: "user",
      text: userDisplayText,
      imageUrl: referenceImageToSend,
      styleTitle: activeStyle?.title,
      aspectRatio,
      assets: [...assets],
      timestamp: Date.now(),
    }

    const aiLoadingMessage: ChatMessage = {
      id: `ai_${Date.now()}`,
      sender: "ai",
      isGenerating: true,
      timestamp: Date.now(),
    }

    setMessages((prev) => [...prev, userMessage, aiLoadingMessage])
    setShowStyleDetails(false)
    setPrompt("")
    const attachedAssets = isCollageStyle ? assets.filter((asset) => asset.kind === 'image') : [...assets]
    if (referenceImageToSend && hasDistinctReference && (!currentProjectId || isAnalysisStyle || isCollageStyle || isTimelineStyle)) {
      // The selected person or source image must be reference 1, before garment or supporting images.
      attachedAssets.unshift({ id: `selected_reference_${Date.now()}`, name: 'reference.png', url: referenceImageToSend, mimeType: 'image/png', sizeBytes: 0, kind: 'image' })
    }
    setAssets([])
    setActiveReference(null)
    setActiveReferencePreset(null)

    if (isRevisingTarget && currentProjectId) {
      // Revise existing generated image
      try {
        const updatedProject = await reviseProject(
          currentProjectId,
          instructionToSend,
          aspectRatio,
          idempotencyKey,
          attachedAssets,
        )

        const latestVersion =
          updatedProject.versions?.[updatedProject.versions.length - 1]
        const newImageUrl = latestVersion?.imageUrl || referenceImageToSend || ""
        const newVersionNum =
          latestVersion?.version || 2

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === aiLoadingMessage.id
              ? {
                  ...msg,
                  isGenerating: false,
                  imageUrl: newImageUrl,
                  version: newVersionNum,
                  projectId: updatedProject.id,
                  styleTitle: activeStyle?.title,
                  aspectRatio,
                }
              : msg,
          ),
        )

        settleCredit(idempotencyKey)
        void haptic("success")
      } catch {
        refundCredit(idempotencyKey)
        setMessages((prev) => prev.filter((msg) => msg.id !== aiLoadingMessage.id))
        setNotice({ title: copy.imageCreation, message: copy.revisionFailed })
      }
    } else {
      // Create brand new project
      try {
        const project = await createProject({
          title: activeStyle?.title,
          mode: targetMode,
          subcategory: targetSubcat,
          styleCategory: activeStyle?.category,
          prompt: instructionToSend || (activeStyle ? 'Create this style.' : copy.defaultPrompt),
          stylePrompt: activeStyle?.realPrompt?.trim()
            || (activeStyle ? `Apply the visual style named "${activeStyle.title}".` : undefined),
          aspectRatio,
          inputAssets: attachedAssets,
        })

        const generatedImageUrl =
          project.versions?.[project.versions.length - 1]?.imageUrl || activeStyle?.imageUrl || ""
        const generatedVersion =
          project.versions?.[project.versions.length - 1]?.version || 1

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === aiLoadingMessage.id
              ? {
                  ...msg,
                  isGenerating: false,
                  imageUrl: generatedImageUrl,
                  version: generatedVersion,
                  projectId: project.id,
                  styleTitle: activeStyle?.title,
                  aspectRatio,
                }
              : msg,
          ),
        )

        settleCredit(idempotencyKey)
        void haptic("success")
      } catch {
        refundCredit(idempotencyKey)
        setMessages((prev) => prev.filter((msg) => msg.id !== aiLoadingMessage.id))
        setNotice({ title: copy.imageCreation, message: copy.generationFailed })
      }
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  const canSubmit = Boolean(
    prompt.trim()
    || assets.length > 0
    || activeReference
    || activeStyle,
  )
  const isExternalStyle = Boolean(activeStyle && styleEntryOrigin === 'external')
  const isGalleryReference = Boolean(activeReference && activeReference.entryOrigin === 'gallery')
  const isExternalLayout = isExternalStyle || isGalleryReference
  const isSubmitted = messages.length > 0 || isGenerating || isNextYearsGenerating
  const quickStart = (
    <section
      className={`studio-chat-quick-start${isExternalLayout ? " has-style" : ""}`}
      aria-label={studioQuickStartIdeasLabel[locale]}
    >
      <ul className="studio-chat-capability-list">
        {quickStartIdeaIndexes.map((index) => (
          <li key={index}>
            <span aria-hidden="true" />
            <button type="button" onClick={() => {
              setPrompt(studioQuickStartIdeas[locale][index]!)
              textareaRef.current?.focus()
              void haptic("selection")
            }}>{studioQuickStartIdeas[locale][index]}</button>
          </li>
        ))}
      </ul>
    </section>
  )

  return (
    <div className="studio-chat-container">
      <div className={`studio-chat-styles-wrapper${isSubmitted ? " is-compact" : ""}${isExternalLayout ? " is-external" : ""}`}>
        {isGalleryReference && activeReference ? (
          <div className={`studio-chat-selected-style-row${isSubmitted ? " is-sent" : ""}`}>
            <div className="studio-chat-style-card studio-chat-selected-style-card is-active">
              <img src={activeReference.imageUrl} alt={activeReference.displayTitle || copy.referenceImage} />
            </div>
            <div className="studio-chat-selected-style-copy">
              <div className="studio-chat-style-info-header">
                <strong className="studio-chat-style-info-title">
                  {activeReference.displayTitle || copy.referenceImage}
                </strong>
                <button type="button" className="studio-chat-style-icon-btn" onClick={handleClearReference} aria-label={copy.removeReference} title={copy.removeReference}>
                  <X size={13} strokeWidth={2.4} />
                </button>
              </div>
              <p className="studio-chat-style-desc-text">{activeReference.description || copy.referenceNotice}</p>
            </div>
          </div>
        ) : isExternalStyle && activeStyle ? (
          <div className={`studio-chat-selected-style-row${isSubmitted ? " is-sent" : ""}`}>
            <button
              type="button"
              className="studio-chat-style-card studio-chat-selected-style-card is-active"
              onClick={() => {
                const task = applicationStyles.find((item) => item.id === activeStyle.id)
                if (task) setInspectStyleTask(task)
              }}
              aria-label={copy.fullScreenStyle}
            >
              <img src={activeStyle.imageUrl} alt="" />
            </button>
            <div className="studio-chat-selected-style-copy">
              <div className="studio-chat-style-info-header">
                <strong className="studio-chat-style-info-title">{activeStyle.title}</strong>
                <button type="button" className="studio-chat-style-icon-btn" onClick={handleClearActiveStyle} aria-label={copy.removeStyle} title={copy.removeStyle}>
                  <X size={13} strokeWidth={2.4} />
                </button>
              </div>
              {activeStyle.description && <p className="studio-chat-style-desc-text">{activeStyle.description}</p>}
            </div>
          </div>
        ) : (
          <>
        <div
          className="studio-chat-styles-rail"
          role="tablist"
          aria-label={copy.styles}
        >
          {applicationStyles.map((style) => {
            const isSelected = activeStyle?.id === style.id
            return (
              <button
                key={`${style.category}-${style.id}`}
                type="button"
                role="tab"
                aria-selected={isSelected}
                className={`studio-chat-style-card${isSelected ? " is-active" : ""}`}
                onClick={() => handleSelectCatalogStyle(style)}
              >
                <img src={style.sampleImageUrl} alt="" loading="lazy" />
                <div className="studio-chat-card-overlay">
                  <span className="studio-chat-card-title">{style.shortTitle || style.title}</span>
                </div>
              </button>
            )
          })}
        </div>
        {showStyleDetails && activeStyle && !inputFocused && (
          <div
            className="studio-chat-style-info-panel"
            role="region"
            aria-label={`Stil: ${activeStyle.title}`}
          >
            <div className="studio-chat-style-info-header">
              <strong className="studio-chat-style-info-title">{activeStyle.title}</strong>
              <div className="studio-chat-style-info-actions">
                <button
                  type="button"
                  className="studio-chat-style-icon-btn"
                  onClick={() => {
                    const task = applicationStyles.find((item) => item.id === activeStyle.id)
                    if (task) setInspectStyleTask(task)
                  }}
                  aria-label={copy.fullScreenStyle}
                  title={copy.fullScreenStyle}
                >
                  <Maximize2 size={13} strokeWidth={2.3} />
                </button>
                <button
                  type="button"
                  className="studio-chat-style-icon-btn"
                  onClick={() => setShowStyleDetails(false)}
                  aria-label={copy.closeDescription}
                  title={copy.closeDescription}
                >
                  <X size={13} strokeWidth={2.4} />
                </button>
              </div>
            </div>
            {activeStyle.description && (
              <p className="studio-chat-style-desc-text">{activeStyle.description}</p>
            )}
            {activeStyle.suggestions.length > 0 && (
              <div className="studio-chat-style-suggestions-flat">
                {activeStyle.suggestions.slice(0, 2).map((suggestion) => (
                  <div className="studio-chat-style-suggestion-bullet" key={suggestion}>
                    <span className="studio-chat-bullet-dot" aria-hidden="true">•</span>
                    <span className="studio-chat-bullet-text">{suggestion}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
          </>
        )}
      </div>

      {/* Chat akışı */}
      <div className="studio-chat-stream" ref={chatStreamRef}>
        {messages.length === 0 && !activeReference && !inputFocused && !activeStyle && quickStart}
        {messages.filter((msg) => msg.sender === "user" || msg.isGenerating || Boolean(msg.imageUrl)).map((msg) => (
          <div
            key={msg.id}
            className={`studio-chat-message-row is-${msg.sender}`}
          >
            {msg.sender === "user" ? (
              <div className="studio-chat-bubble-user">
                {msg.imageUrl && (
                  <div className="studio-chat-user-image-wrap">
                    <img
                      src={msg.imageUrl}
                      alt={copy.submittedImage}
                      onLoad={() => {
                        if (chatStreamRef.current) {
                          chatStreamRef.current.scrollTop = chatStreamRef.current.scrollHeight
                        }
                      }}
                    />
                  </div>
                )}
                {msg.assets && msg.assets.length > 0 && (
                  <div className="studio-chat-user-assets">
                    {msg.assets.map((ast) => (
                      <img key={ast.id} src={ast.url} alt={ast.name} />
                    ))}
                  </div>
                )}
                {msg.text && <p className="studio-chat-user-text">{msg.text}</p>}
                {(msg.styleTitle || msg.aspectRatio) && (
                  <div className="studio-chat-user-meta">
                    {msg.styleTitle && <span>{msg.styleTitle}</span>}
                    {msg.aspectRatio && <span>{msg.aspectRatio}</span>}
                  </div>
                )}
              </div>
            ) : (
              <div className="studio-chat-bubble-ai">
                {msg.isGenerating ? (
                  <div className="studio-chat-ai-loading">
                    <div className="studio-chat-generation-status">
                      <span className="studio-chat-generation-mark" aria-hidden="true"><VoxnelaMark animated /></span>
                      <span className="studio-chat-generation-label">{copy.generating}</span>
                    </div>
                    <video
                      className="studio-chat-generation-video"
                      src="/generation/visual.mp4"
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="auto"
                      aria-hidden="true"
                      onCanPlay={(event) => { void event.currentTarget.play().catch(() => {}) }}
                    />
                  </div>
                ) : (
                  <div className="studio-chat-ai-result">
                    {msg.imageUrl && (
                      <div
                        className="studio-chat-image-inline"
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          void haptic("selection")
                          const idx = chatImageItems.findIndex(
                            (it) => it.id === msg.id || it.imageUrl === msg.imageUrl,
                          )
                          setActiveDetailIndex(idx >= 0 ? idx : 0)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault()
                            void haptic("selection")
                            const idx = chatImageItems.findIndex(
                              (it) => it.id === msg.id || it.imageUrl === msg.imageUrl,
                            )
                            setActiveDetailIndex(idx >= 0 ? idx : 0)
                          }
                        }}
                        aria-label={copy.viewImage}
                        title={copy.viewImage}
                      >
                        <img
                          src={imageUrlForMessage(msg)}
                          alt={copy.generatedImage}
                          onLoad={() => {
                            if (chatStreamRef.current) {
                              chatStreamRef.current.scrollTop = chatStreamRef.current.scrollHeight
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Chat giriş alanı */}
      <div className="studio-chat-composer">
        {isExternalLayout && messages.length === 0 && !inputFocused && quickStart}
        {activeStyle && !isExternalStyle && (
          <div className="studio-chat-active-style" role="status">
            <img
              className="studio-chat-active-style-thumb"
              src={activeStyle.imageUrl}
              alt=""
            />
            <strong>{activeStyle.title}</strong>
            <button
              type="button"
              onClick={handleClearActiveStyle}
              aria-label={copy.removeStyle}
              title={copy.removeStyle}
            >
              <X size={13} strokeWidth={2.4} />
            </button>
          </div>
        )}
        {/* REFERANS GÖRSEL ŞERİDİ (Revizyon / Stil Değiştirme İçin) */}
        {activeReference && !isGalleryReference && (
          <div className="studio-chat-reference-strip" role="status">
            <div className="studio-chat-reference-content">
              <img
                src={activeReference.imageUrl}
                alt={copy.referenceImage}
                className="studio-chat-reference-thumb"
              />
              <div className="studio-chat-reference-labels">
                <strong className="studio-chat-reference-title">
                  {activeReference.label || copy.referenceImage}
                </strong>
                <span className="studio-chat-reference-desc">
                  · {copy.referenceNotice}
                </span>
              </div>
            </div>
            <button
              type="button"
              className="studio-chat-reference-remove"
              onClick={handleClearReference}
              aria-label={copy.removeReference}
              title={copy.removeReference}
            >
              <X size={13} strokeWidth={2.4} />
            </button>
          </div>
        )}


        {(assets.length > 0 || pendingUploads.length > 0) && (
          <div className="studio-chat-attachment-bar">
            {assets.map((asset) => (
              <div key={asset.id} className="studio-chat-attachment-chip">
                {asset.kind === "image" ? (
                  <img src={asset.url} alt={asset.name} />
                ) : (
                  <FileText size={14} style={{ color: "var(--color-primary-400, #e05345)" }} />
                )}
                <span className="studio-chat-attachment-name" title={asset.name}>{asset.name}</span>
                <button
                  type="button"
                  onClick={() => removeAsset(asset.id)}
                  aria-label={copy.remove}
                >
                  <X size={12} />
                </button>
              </div>
            ))}

            {pendingUploads.map((pending) => (
              <div
                key={pending.id}
                className="studio-chat-attachment-chip is-skeleton"
                aria-label={copy.uploading}
                role="status"
              >
                <div className="studio-chip-skeleton-thumb" />
                <div className="studio-chip-skeleton-text" />
              </div>
            ))}
          </div>
        )}

        <div className="studio-chat-input-capsule">
          {/* Dosya Ekleme İkonu (Ataç) */}
          <button
            type="button"
            className="studio-chat-action-icon-btn"
            onClick={() => openAssetPicker()}
            disabled={uploading}
            aria-label={copy.addFile}
            title={copy.addFileTitle}
          >
            {uploading ? (
              <span className="studio-upload-logo-wrap" aria-label={copy.uploading} role="status">
                <VoxnelaMark animated />
              </span>
            ) : (
              <Plus size={19} strokeWidth={2.8} />
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ALL_ASSET_TYPES}
            multiple
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          {/* En Boy Açılır Menüsü (Ataçın hemen yanında) */}
          <div className="studio-chat-aspect-wrap" ref={aspectMenuRef}>
            <button
              type="button"
              className={`studio-chat-aspect-btn${aspectMenuOpen ? " is-active" : ""}`}
              onClick={() => {
                void haptic("selection")
                setAspectMenuOpen((prev) => !prev)
              }}
              aria-label={`${copy.aspectRatio}: ${selectedAspectRatio}`}
              title={copy.selectAspectRatio}
              aria-expanded={aspectMenuOpen}
              aria-haspopup="listbox"
            >
              <span className="studio-chat-aspect-text">{selectedAspectRatio}</span>
              <ChevronDown
                size={12}
                strokeWidth={2.4}
                className={`studio-chat-aspect-chevron${aspectMenuOpen ? " is-open" : ""}`}
              />
            </button>

            {aspectMenuOpen && (
              <div
                className="studio-chat-aspect-dropdown"
                role="listbox"
                aria-label={copy.aspectOptions}
              >
                <div className="studio-chat-aspect-dropdown-header">{copy.aspectRatio}</div>
                {ASPECT_RATIO_OPTIONS.map((opt) => {
                  const isSelected = opt.id === selectedAspectRatio
                  const localizedAspect = {
                    '1:1': [copy.aspectSquare, copy.aspectSquareDetail],
                    '3:4': [copy.aspectPortrait, copy.aspectPortraitDetail],
                    '4:3': [copy.aspectHorizontal, copy.aspectHorizontalDetail],
                    '9:16': [copy.aspectVertical, copy.aspectVerticalDetail],
                    '16:9': [copy.aspectHorizontal, copy.aspectHorizontalDetail],
                    '4:5': [copy.aspectPortrait, copy.aspectPortraitDetail],
                    '5:4': [copy.aspectHorizontal, copy.aspectHorizontalDetail],
                    '9:21': [copy.aspectVertical, copy.aspectVerticalDetail],
                    '21:9': [copy.aspectPanoramic, copy.aspectPanoramicDetail],
                  }[opt.id] ?? [opt.label, opt.description]
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={`studio-chat-aspect-item${isSelected ? " is-selected" : ""}`}
                      onClick={() => {
                        void haptic("selection")
                        setSelectedAspectRatio(opt.id)
                        setAspectMenuOpen(false)
                      }}
                    >
                      <div className="studio-chat-aspect-item-icon-box">
                        <span className={`studio-chat-aspect-ratio-preview is-${opt.id.replace(":", "-")}`} />
                      </div>
                      <div className="studio-chat-aspect-item-labels">
                        <span className="studio-chat-aspect-item-title">{localizedAspect[0]}</span>
                        <span className="studio-chat-aspect-item-sub">{localizedAspect[1]}</span>
                      </div>
                      <span className="studio-chat-aspect-item-tag">{opt.ratioLabel}</span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Text Alanı */}
          <textarea
            ref={textareaRef}
            className="studio-chat-textarea"
            rows={1}
            enterKeyHint="send"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            onPointerDown={() => {
              setInputFocused(true)
              setShowStyleDetails(false)
            }}
            onFocus={() => {
              setInputFocused(true)
              setShowStyleDetails(false)
            }}
            onBlur={() => setInputFocused(false)}
            placeholder={studioPremiumPlaceholder[locale]}
          />

          {/* Send İkonu (100% Dairesel) */}
          <button
            type="button"
            className={`studio-chat-send-btn${canSubmit ? " is-ready" : ""}`}
            style={{
              borderRadius: "50%",
              width: 36,
              height: 36,
              minWidth: 36,
              minHeight: 36,
            }}
            onClick={() => { void handleSend() }}
            disabled={!canSubmit || isGenerating || isRevising || isNextYearsGenerating || uploading}
            aria-busy={isGenerating || isRevising || isNextYearsGenerating}
            aria-label={isGenerating || isRevising
              ? copy.generating
              : (activeReference ? copy.reviseImage : copy.generateImage)}
          >
            {isGenerating || isRevising ? (
              <span className="studio-upload-logo-wrap" aria-hidden="true">
                <VoxnelaMark animated />
              </span>
            ) : (
              <ArrowUp size={19} strokeWidth={2.8} />
            )}
          </button>
        </div>
      </div>

      {/* Görsel detay bottom sheet */}
      <StudioImageDetailSheet
        open={activeDetailIndex !== null && chatImageItems.length > 0}
        items={chatImageItems}
        initialIndex={activeDetailIndex ?? 0}
        onClose={() => setActiveDetailIndex(null)}
        onEdit={(item) => {
          void haptic("selection")
          setActiveStylePreset(null)
          setActiveReference({
            projectId: item.projectId || activeProjectId || undefined,
            imageUrl: item.imageUrl,
            version: item.version,
            label: item.version ? `v${item.version} ${copy.versionImage}` : copy.generatedImage,
            entryOrigin: 'gallery',
            displayTitle: item.project
              ? getProjectStyleTitle(item.project, copy.defaultStyle, locale)
              : item.title || copy.generatedImage,
            description: copy.revisePlaceholder,
          })
          setMessages([])
          setAssets([])
          setPrompt('')
          setInputFocused(false)
          setActiveDetailIndex(null)
        }}
      />

      <TaskShowcaseModal
        open={Boolean(inspectStyleTask)}
        task={inspectStyleTask}
        tasks={applicationStyles}
        onClose={() => setInspectStyleTask(null)}
        onUseTask={(task) => {
          handleSelectCatalogStyle(task)
          setInspectStyleTask(null)
        }}
      />

      <VoxnelaAlertModal
        open={Boolean(generationConfirmation)}
        title={confirmationCopy.title}
        message={confirmationCopy.message}
        primaryLabel={confirmationCopy.confirm}
        secondaryLabel={confirmationCopy.cancel}
        onPrimary={() => {
          if (confirmationApprovedRef.current) return
          confirmationApprovedRef.current = true
          setGenerationConfirmation(null)
          void handleSend(true)
        }}
        onSecondary={() => setGenerationConfirmation(null)}
        onClose={() => setGenerationConfirmation(null)}
      >
        <div className="studio-generation-confirmation">
          <div className="studio-generation-confirmation-field">
            <span>{confirmationCopy.style}</span>
            <strong>{generationConfirmation?.styleTitle || confirmationCopy.noStyle}</strong>
          </div>
          <div className="studio-generation-confirmation-field">
            <span>{confirmationCopy.instructions}</span>
            <p>{generationConfirmation?.instructions || confirmationCopy.noInstructions}</p>
          </div>
          <div className="studio-generation-confirmation-credits">
            <span>{membershipSyncPending || membershipLoading || workspaceError ? paywallMessages[locale].membershipSyncPending : <>{confirmationCopy.credits}: <strong>{credits}</strong></>}</span>
            <span>{confirmationCopy.cost}: <strong>1 {confirmationCopy.creditUnit}</strong></span>
          </div>
        </div>
      </VoxnelaAlertModal>

      <VoxnelaAlertModal
        open={Boolean(notice)}
        title={notice?.title || ''}
        message={notice?.message}
        primaryLabel={copy.done}
        onPrimary={() => setNotice(null)}
        onClose={() => setNotice(null)}
      />

      {/* Kota bitti uyarısı */}
      <QuotaExhaustedModal
        open={quotaModalOpen}
        onClose={() => setQuotaModalOpen(false)}
        membership={membership}
        onOpenPaywall={(opts) => {
          setQuotaModalOpen(false)
          setPaywallInitialPlan(opts?.initialPlan)
          setPaywallOpen(true)
        }}
      />

      {/* 6. PAYWALL SHEET */}
      <PaywallSheet
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        initialPlan={paywallInitialPlan}
        reason="quota"
      />
    </div>
  )
}
